import * as React from "react";
import { Eye, EyeOff, CheckCircle2, Circle } from "lucide-react";

import { Input, type InputProps } from "./input";
import { getPasswordConditions, type PasswordRequirementLabels } from "./password-requirements";

type PasswordInputPropsBase = Omit<InputProps, "type" | "suffix">;

export type PasswordInputProps = PasswordInputPropsBase &
  (
    | { showStrengthRequirements?: false; requirementLabels?: never }
    | { showStrengthRequirements: true; requirementLabels: PasswordRequirementLabels }
  );

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ showStrengthRequirements = false, requirementLabels, onChange, value, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState("");

    React.useEffect(() => {
      if (value !== undefined) {
        setInternalValue(String(value));
      }
    }, [value]);

    const passwordValue = value !== undefined ? String(value) : internalValue;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (showStrengthRequirements) {
        setInternalValue(event.target.value);
      }
      onChange?.(event);
    };

    const input = (
      <Input
        type={showPassword ? "text" : "password"}
        ref={ref}
        value={value}
        onChange={handleChange}
        suffix={
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="text-surface-on-variant dark:text-[#C3C9B4] hover:text-surface-on dark:hover:text-white transition-colors focus:outline-none"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        }
        {...props}
      />
    );

    if (!showStrengthRequirements || !requirementLabels) {
      return input;
    }

    const conditions = getPasswordConditions(passwordValue, requirementLabels);

    return (
      <div className="flex flex-col gap-2">
        {input}
        <div className="flex flex-col gap-1.5 px-1 py-1">
          {conditions.map((condition) => (
            <div key={condition.id} className="flex items-center gap-2">
              {condition.fulfilled ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-success text-pr shrink-0" />
              ) : (
                <Circle className="h-3.5 w-3.5 text-surface-on-variant/40 dark:text-[#C3C9B4]/40 shrink-0" />
              )}
              <span
                className={`text-xs ${condition.fulfilled ? "text-success" : "text-surface-on-variant dark:text-[#C3C9B4]"}`}
              >
                {condition.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
